import React, { useContext } from 'react';
import { Tooltip } from 'react-tooltip';
import _isEmpty from 'lodash/isEmpty';
import Tag from '@app/component/tag/Tag';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import {
	AvailabilityDailyStateContext
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/provider/AvailabillityDailyStateProvider';
import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/20/solid';
import Utilities from '@util/Utilities';


const AvailabilityStateDailySnapshotTooltip: React.FunctionComponent<{
	id: string,
	openOnClick?: boolean
}> = ({ id, openOnClick = true }) => {

	const { companyHotel } = useSelectedCompanyHotel();
	const { availabilityDailyState, total, capacity, findState } = useContext(AvailabilityDailyStateContext);
	const comparisonDays = companyHotel.settings.calendar.numberOfDaysForPriceParity ?? 7;

	if (!availabilityDailyState || !availabilityDailyState.current) return null;

	const previousTotal = availabilityDailyState.previous?.items?.reduce((acc, room) => acc + room.availability, 0);
	const totalDifference = previousTotal === undefined ? undefined : total - previousTotal;

	const renderDifference = (difference: number | undefined, showComparisonLabel = false) => {
		if (difference === undefined) return <span className="text-xs text-gray-400">Comparison unavailable</span>;
		if (difference === 0) {
			return showComparisonLabel ? <span className="text-xs text-gray-400">0 vs {comparisonDays}d</span> : null;
		}

		const Icon = difference > 0 ? ArrowUpIcon : ArrowDownIcon;
		const colorClassName = difference > 0 ? 'text-emerald-500' : 'text-rose-500';

		return <span className={`inline-flex items-center gap-x-0.5 text-xs font-medium ${colorClassName}`}>
			<Icon className="h-3 w-3" />
			<span>{Math.abs(difference)}</span>
			{showComparisonLabel && <span className="ml-0.5 text-[10px] font-medium text-slate-400">vs {comparisonDays}d</span>}
		</span>;
	};

	return (
		<>
			<span className="inline-flex items-center gap-x-1.5">
				<Tag state={findState(companyHotel, total)} extraClassName="cursor-pointer ml-2">
					<span data-tooltip-id={id}>{total}</span>
				</Tag>
			</span>
			<Tooltip
				id={id}
				openOnClick={openOnClick}
				opacity={100}
				className="z-40 !rounded-lg !bg-white !p-0 !opacity-100 shadow-xl ring-1 ring-black ring-opacity-5"
				style={{ backgroundColor: 'white' }}>
				<div className="w-72 overflow-hidden rounded-lg bg-white text-xs text-slate-500">
					<div className="flex items-start justify-between gap-x-4 px-4 py-3">
						<div className="flex items-baseline gap-x-2">
							<span className="text-xl font-semibold text-slate-900">
								{total}{capacity != null && <span className="text-xs font-semibold text-slate-400"> / {capacity}</span>}
							</span>
							<span className="font-medium text-slate-500">rooms available</span>
						</div>
						<div className="pt-1">{renderDifference(totalDifference, true)}</div>
					</div>
					<div className="border-t border-slate-100 px-4 py-3">
						<div className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-slate-400">By room type</div>
						<div className="space-y-2">
							{!_isEmpty(availabilityDailyState.current.items)
								&& availabilityDailyState.current.items.map((room, idx) => {


									return <div key={idx} className="flex items-center justify-between gap-x-4">
										<span className="min-w-0 truncate text-xs font-medium text-slate-600">{room.roomName}</span>
										<span className="shrink-0 text-xs font-semibold text-slate-900">
											{room.availability}

										</span>
									</div>;
								})}
						</div>
					</div>
					<div className="border-t border-slate-100 bg-slate-50 px-4 py-2 text-[10px] font-medium text-slate-400">
						PMS · Updated hourly · {Utilities.timeDifference(availabilityDailyState.current.creationDate)} ago
					</div>
				</div>
			</Tooltip>
		</>
	);
};

export default AvailabilityStateDailySnapshotTooltip;
